import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["playlistId", "showLoader", "appearsIn", "location"];
import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getListingUrl, getPlaylistUrl } from '../../../utils/RoutingUtils';
import { usePlaylistProgress, useStartPlaylistProgress } from '../../userProgress/userProgressQueries';
import { useFullPlaylist, useUserOwnsPlaylist } from '../savedItemsQueries';
import { useLocation } from 'react-router-dom';
import UsageTracker from '../../../utils/UsageTracker';
import Raven from 'raven-js';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { convertToLegacyLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const useFindNextContent = (playlistId, appearsIn) => {
  const {
    pathname
  } = useLocation();
  const {
    playlistProgress
  } = usePlaylistProgress(playlistId);
  const {
    playlist,
    displayedPlaylistItems
  } = useFullPlaylist(playlistId);
  const isAssignedPlaylist = playlist && playlist.assignments && playlist.assignments.length > 0 || false;
  const nextItem = useMemo(() => {
    const playlistItems = displayedPlaylistItems || [];
    const progressItems = playlistProgress && playlistProgress.itemsProgress || [];
    const inProgressItems = progressItems.filter(item => item.status !== 'COMPLETED');
    if (
    /**
     * There are no in progress items and all items are completed
     */
    inProgressItems.length === 0 && progressItems.length === playlistItems.length) {
      return Object.assign({}, playlistItems[0], {
        isStarted: true
      });
    } else if (
    /**
     * There are no in progress items, some completed items and possibly some
     * not started items we want to look for
     */
    inProgressItems.length === 0 && progressItems.length !== playlistItems.length) {
      const notStartedItem = playlistItems.find(item => !progressItems.find(progressItem => progressItem.itemId === item.itemId && progressItem.type === item.type));
      if (notStartedItem) {
        return Object.assign({}, notStartedItem, {
          isStarted: false
        });
      }
    } else {
      /**
       * There are in progress items and we want to find the most recently progressed item.
       */
      const progressItem = inProgressItems[0];
      const newSelectedItem = playlistItems.find(item => item.itemId === progressItem.itemId && item.type === progressItem.type);
      if (newSelectedItem) {
        return Object.assign({}, newSelectedItem, {
          isStarted: true
        });
      }
    }
    return Object.assign({}, playlistItems[0], {
      isStarted: true
    });
  }, [displayedPlaylistItems, playlistProgress]);
  if (!nextItem || !playlistProgress || !playlist) return null;
  const legacyLanguage = nextItem.language ? convertToLegacyLanguage(nextItem.language) : 'en';
  const url = nextItem.type && nextItem.itemId ? getListingUrl({
    isStarted: nextItem.isStarted,
    type: nextItem.type,
    id: nextItem.itemId,
    language: legacyLanguage,
    playlistId,
    isAssignedPlaylist,
    currentPathname: pathname,
    appearsIn
  }).fullUrl : '';
  return {
    url,
    language: legacyLanguage,
    type: nextItem.type,
    itemId: nextItem.itemId
  };
};
const getButtonText = status => {
  if (status === 'IN_PROGRESS') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.savedItems.playlistDetails.continuePlaylist"
    });
  }
  if (status === 'COMPLETED') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.savedItems.playlistDetails.completedPlaylist"
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.savedItems.playlistDetails.startPlaylist"
  });
};
export const usePlaylistButtonState = (playlistId, appearsIn) => {
  const {
    displayedPlaylistItems
  } = useFullPlaylist(playlistId);
  const {
    playlistProgress,
    playlistProgressLoading
  } = usePlaylistProgress(playlistId);
  const userIsOwner = useUserOwnsPlaylist(playlistId);
  const nextContent = useFindNextContent(playlistId, appearsIn);
  const [startPlaylistProgress] = useStartPlaylistProgress({
    playlistId,
    language: (nextContent === null || nextContent === void 0 ? void 0 : nextContent.language) || 'en'
  });
  const inProgress = (playlistProgress === null || playlistProgress === void 0 ? void 0 : playlistProgress.status) === 'IN_PROGRESS';
  const buttonText = getButtonText(playlistProgress === null || playlistProgress === void 0 ? void 0 : playlistProgress.status);
  const isLoading = playlistProgressLoading;
  const isDisabled = isLoading || !(displayedPlaylistItems !== null && displayedPlaylistItems !== void 0 && displayedPlaylistItems.length) || !(nextContent !== null && nextContent !== void 0 && nextContent.url);
  const handlePlaylistStart = location => {
    if (!inProgress && nextContent) {
      startPlaylistProgress().catch(e => {
        Raven.captureException(e);
      });
    }
    UsageTracker.track('playlistInteraction', {
      action: inProgress ? 'continue-playlist' : 'start-playlist',
      isOwner: userIsOwner,
      playlistId,
      location
    });
  };
  return {
    url: (nextContent === null || nextContent === void 0 ? void 0 : nextContent.url) || '',
    buttonText,
    inProgress,
    isLoading,
    isDisabled,
    playlistProgress,
    nextContent,
    handlePlaylistStart
  };
};
const StartPlaylistButton = _ref => {
  let {
      playlistId,
      showLoader,
      appearsIn,
      location = 'playlist-details'
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    url,
    buttonText,
    isLoading,
    isDisabled,
    playlistProgress,
    nextContent,
    handlePlaylistStart
  } = usePlaylistButtonState(playlistId, appearsIn);
  if (!showLoader && (!playlistProgress || !nextContent)) return null;
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !isDisabled || isLoading,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.savedItems.playlistDetails.startPlaylistDisabledTooltip"
    }),
    children: /*#__PURE__*/_jsx(RouterButton, Object.assign({
      disabled: isDisabled,
      onClick: () => handlePlaylistStart(location),
      to: url || getPlaylistUrl(playlistId),
      use: "primary"
    }, props, {
      children: /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx("span", {
          style: {
            opacity: isLoading || props.use === 'unstyled' && isDisabled ? 0.3 : 1,
            flex: showLoader ? undefined : 1
          },
          children: buttonText
        }), showLoader && isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
          margin: "0",
          size: "extra-small"
        })]
      })
    }))
  });
};
export default StartPlaylistButton;